import supportedLocales from '../../../supported-locales.json';

type AemPageMetadata = {
  'cq:redirectTarget'?: unknown;
};

type AemPageModel = {
  vaaPageMetaData?: AemPageMetadata;
};

const DEFAULT_LOCALE = 'en-GB';
const VAA_HOME_TARGET_PATTERN =
  /^\/content\/vaa-home\/([^/?#]+)\/([^/?#]+)(\/[^?#]*)?$/;

const isSchemeRelativeRoute = (route: string): boolean =>
  route.startsWith('//') || route.startsWith('/\\');

function getVaaHomePublicRoute(redirectTarget: string): string | null {
  const targetMatch = redirectTarget.match(VAA_HOME_TARGET_PATTERN);
  if (!targetMatch) return null;

  const [, region, language, pagePath = ''] = targetMatch;
  if (!region || !language) return null;

  const normalizedPagePath = pagePath === '/' ? '' : pagePath;

  if (region.toLowerCase() === 'global') {
    if (language.toLowerCase() !== 'en') return null;

    const publicRoute = normalizedPagePath || '/';
    return isSchemeRelativeRoute(publicRoute) ? null : publicRoute;
  }

  const targetLocale = `${language.toLowerCase()}-${region.toUpperCase()}`;
  const supportedLocale = supportedLocales.find(
    locale => locale.toLowerCase() === targetLocale.toLowerCase(),
  );

  if (!supportedLocale) return null;

  if (supportedLocale === DEFAULT_LOCALE) {
    const publicRoute = normalizedPagePath || '/';
    return isSchemeRelativeRoute(publicRoute) ? null : publicRoute;
  }

  return `/${supportedLocale}${normalizedPagePath}`;
}

/**
 * Converts AEM page redirect metadata into a public Browse route.
 * VAA Home targets retain their authored locale, while legacy non-VAA targets
 * continue to use the existing AEM-prefix stripping behaviour.
 */
export function getAemRedirectUrl(aemData: unknown): string | null {
  if (!aemData || typeof aemData !== 'object') return null;

  const metadata = (aemData as AemPageModel).vaaPageMetaData;
  if (!metadata || typeof metadata !== 'object') return null;

  const redirectTarget = metadata['cq:redirectTarget'];
  if (typeof redirectTarget !== 'string') return null;

  if (
    redirectTarget.startsWith('http://') ||
    redirectTarget.startsWith('https://')
  ) {
    return redirectTarget;
  }

  if (redirectTarget.startsWith('/content/vaa-home/')) {
    return getVaaHomePublicRoute(redirectTarget);
  }

  const redirectUrl = `/${redirectTarget.split('/').slice(5).join('/')}`;

  return redirectUrl &&
    redirectUrl !== '/' &&
    !isSchemeRelativeRoute(redirectUrl)
    ? redirectUrl
    : null;
}
